import {
  ColumnFactory,
  createDataQualityCellFormatter,
  createIconCellFormatter,
  createServerOrPathString,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  DatabaseMediumIcon,
  DataQualityCellFormatterRowDataFunctions,
  DataQualityIconHeaderCertificationFormatterFactory,
  DataQualityIconHeaderFormatterFactory,
  ExplorerStyledDisplayGrid,
  FeatureFlagsContext,
  getDatabaseIconTooltip,
  GraphQLError,
  GridSortProps,
  mapDC3ColumnFactoriesToVizPortalColumnFactories,
  messageForConnectionType,
  reportGraphQLErrors,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { Formatters } from '@tableau/widgets/lib/gen-src/Localize';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { SvgGroupPaperclip } from '../../../gen-src/icons/GroupPaperclip';
import { DatabaseOrderField } from '../../../gen-src/queries/globalTypes';
import { DatabasesDisplayGridRowData } from '../database/types/DatabasesDisplayGridRowData';
import {
  createObfuscatedLinkWithIconCellFormatter,
  createObfuscatedTextCellFormatter,
  PermissionsRequiredCell,
} from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createProjectLocationLinkCellFormatter } from '../datagrid/cellFormatters/ProjectLocationCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import {
  GlobalAssetGridDC3ColumnFactoryParams,
  makeDC3ColumnFactoryParamsFromVizPortalParams,
} from '../sorting/makeColumnFactoryParams';
import { DatabasesValidSortFields } from '../sorting/sortProviders/DatabasesGridSortProvider';
import { CellFormatter } from './types/SharedDisplayGridTypes';

const shouldBeObfuscated: (rowData: DatabasesDisplayGridRowData) => boolean = ({
  name,
}) => name === null;

type DatabasesColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<DatabasesValidSortFields>,
  DatabasesDisplayGridRowData,
  DatabasesValidSortFields
>;
interface IconColumnDatabaseParams {
  rowData: DatabasesDisplayGridRowData;
}

function iconColumn(params: IconColumnDatabaseParams): JSX.Element {
  const paramsOverride: IconColumnDatabaseParams = {
    ...params,
    rowData: {
      ...params.rowData,
      isCertified: false,
    },
  };
  return <DatabaseMediumIcon database={paramsOverride.rowData} />;
}

function databaseGroupIcon(
  rowData: DatabasesDisplayGridRowData
): JSX.Element | null {
  if (!rowData.isGrouped) {
    return null;
  }

  return (
    <div
      role='img'
      aria-label={Messages.GroupedAsset()}
      title={Messages.GroupedAsset()}
    >
      <SvgGroupPaperclip />
    </div>
  );
}

function databaseLinkTitle(
  rowData: DatabasesDisplayGridRowData
): string | null {
  return rowData.name;
}

const rowDataFunctions: DataQualityCellFormatterRowDataFunctions<
  DatabasesDisplayGridRowData
> = {
  getAssetUrlFromRowData: (urls: SiteUrls, r: DatabasesDisplayGridRowData) =>
    urls.catalogDatabaseUrl(r.id),
  getNameFromRowData: (r: DatabasesDisplayGridRowData) =>
    r.name ? r.name : '',
  getTypeNameFromRowData: (r: DatabasesDisplayGridRowData) => r.__typename,
  getDataQualityCertificationFromRowData: (r: DatabasesDisplayGridRowData) =>
    r.dataQualityCertifications,
};

export const columns: ReadonlyArray<DatabasesColumnFactory> = [
  (sortParams) => ({
    name: 'type',
    dataKey: DatabaseOrderField.IS_CERTIFIED,
    headerText: Messages.GridHeaderType(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createIconCellFormatter(
      (rowData) =>
        iconColumn({
          rowData: rowData,
        }),
      (rowData) => (rowData.isCertified ? '' : getDatabaseIconTooltip(rowData))
    ),
    sortable: true,
    resizable: false,
  }),
  () => ({
    name: 'certification',
    dataKey: UnsortableColumnKey,
    headerText: '',
    headerTooltip: Messages.Certification(),
    headerFormatter: DataQualityIconHeaderCertificationFormatterFactory,
    cellFormatter: createDataQualityCellFormatter({
      getWarningsFromRowData: (r: DatabasesDisplayGridRowData) =>
        r.dataQualityWarnings,
      disableIconOnlyTabIndex: true,
      isCertification: true,
      rowDataFunctions: rowDataFunctions,
    }),
    sortable: false,
    resizable: false,
  }),
  () => ({
    name: 'warning',
    dataKey: UnsortableColumnKey,
    headerText: '',
    headerTooltip: Messages.DataQualityWarningTitle(),
    headerFormatter: DataQualityIconHeaderFormatterFactory,
    cellFormatter: createDataQualityCellFormatter({
      getWarningsFromRowData: (r) => {
        if (r.id === '') {
          return undefined;
        } else {
          return r.dataQualityWarnings;
        }
      },
      disableIconOnlyTabIndex: true,
      isCertification: false,
      rowDataFunctions: rowDataFunctions,
    }),
    sortable: false,
    resizable: false,
  }),
  (params) => ({
    name: 'name',
    dataKey: DatabaseOrderField.NAME,
    headerText: Messages.GridHeaderName(),
    headerFormatter: createSortableHeaderFormatter(params),
    cellFormatter: createObfuscatedLinkWithIconCellFormatter(
      shouldBeObfuscated,
      ({ name }) => name, // getTextFromRowData
      (urls, { id }) => urls.catalogDatabasePath(id), // getPathFromRowData
      false, // noPadding,
      true, // disableTabIndex
      PermissionsRequiredCell, // ObfuscatedComponent
      databaseGroupIcon,
      databaseLinkTitle,
      (rowData) => rowData.id === '' // graphQLId
    ),
    growToFit: true,
    resizable: true,
    sortable: true,
  }),
  (sortParams) => ({
    name: 'virtualConnections',
    dataKey: DatabaseOrderField.DOWNSTREAM_VIRTUAL_CONNECTIONS_COUNT,
    headerText: Messages.GridHeaderVirtualConnection(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createTextCellFormatter(
      ({ downstreamVirtualConnectionsConnection }) =>
        downstreamVirtualConnectionsConnection
          ? Formatters.formatCount(
              downstreamVirtualConnectionsConnection.totalCount
            )
          : null
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'workbooks',
    dataKey: DatabaseOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
    headerText: Messages.GridHeaderWorkbooks(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createTextCellFormatter(
      ({ downstreamWorkbooksConnection }) =>
        downstreamWorkbooksConnection
          ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
          : null
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'datasources',
    dataKey: DatabaseOrderField.DOWNSTREAM_DATASOURCES_COUNT,
    headerText: Messages.GridHeaderDatasources(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createTextCellFormatter(
      ({ downstreamDatasourcesConnection }) =>
        downstreamDatasourcesConnection
          ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
          : null
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'serverOrPath',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderServerOrPath(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createObfuscatedTextCellFormatter<
      DatabasesDisplayGridRowData
    >(
      shouldBeObfuscated,
      createServerOrPathString,
      undefined, // placeholder
      (rowData) => rowData.id === ''
    ),
    resizable: true,
    sortable: false,
  }),
  (sortParams) => ({
    name: 'connectionType',
    dataKey: DatabaseOrderField.CONNECTION_TYPE,
    headerText: Messages.GridHeaderConnectionType(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createObfuscatedTextCellFormatter(
      shouldBeObfuscated,
      (rowData) => messageForConnectionType(rowData.connectionType),
      undefined, // placeholder
      (rowData) => rowData.id === ''
    ),
    resizable: true,
    sortable: true,
  }),
  (sortParams) => ({
    name: 'contact',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderContact(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createUserNameLinkCellFormatter(
      ({ contact }) => contact,
      true,
      (rowData) => rowData.id === ''
    ),
    resizable: true,
    sortable: false,
  }),
];

/**
 * @deprecated These cell formatters are already in use in the columns above. These defs below will be removed once we have
 * the columns used properly in vpc.
 */
const cellFormattersForCrass: Record<string, CellFormatter> = {
  dataQualityWarning: {
    type: 'database',
    cellFormatter: createDataQualityCellFormatter({
      getWarningsFromRowData: (r) => {
        if (r.id === '') {
          return undefined;
        } else {
          return r.dataQualityWarnings;
        }
      },
      disableIconOnlyTabIndex: true,
      isCertification: false,
      rowDataFunctions: rowDataFunctions,
    }),
  },
  typeForCrass: {
    type: 'database',
    cellFormatter: createIconCellFormatter(
      (rowData) =>
        iconColumn({
          rowData: rowData,
        }),
      (rowData) => (rowData.isCertified ? '' : getDatabaseIconTooltip(rowData))
    ),
  },
  name: {
    type: 'database',
    cellFormatter: createObfuscatedLinkWithIconCellFormatter(
      shouldBeObfuscated,
      ({ name }) => name, // getTextFromRowData
      (urls, { id }) => urls.catalogDatabasePath(id), // getPathFromRowData
      false, // noPadding,
      true, // disableTabIndex
      PermissionsRequiredCell, // ObfuscatedComponent
      databaseGroupIcon,
      databaseLinkTitle,
      (rowData) => rowData.id === ''
    ),
  },
  downstreamWorkbookCount: {
    type: 'database',
    cellFormatter: createTextCellFormatter(
      ({ downstreamWorkbooksConnection }) =>
        downstreamWorkbooksConnection
          ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
          : null
    ),
  },
  downstreamDataSourcesCount: {
    type: 'database',
    cellFormatter: createTextCellFormatter(
      ({ downstreamDatasourcesConnection }) =>
        downstreamDatasourcesConnection
          ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
          : null
    ),
  },
  serverOrPath: {
    type: 'database',
    cellFormatter: createObfuscatedTextCellFormatter<
      DatabasesDisplayGridRowData
    >(
      shouldBeObfuscated,
      createServerOrPathString,
      undefined, // placeholder
      (rowData) => rowData.id === ''
    ),
  },
  connectionTypeCrass: {
    type: 'database',
    cellFormatter: createObfuscatedTextCellFormatter(
      shouldBeObfuscated,
      (rowData) => messageForConnectionType(rowData.connectionType),
      undefined, // placeholder
      (rowData) => rowData.id === ''
    ),
  },
  contact: {
    type: 'database',
    cellFormatter: createUserNameLinkCellFormatter(
      ({ contact }) => contact,
      true,
      (rowData) => rowData.id === ''
    ),
  },
  databaseProjectLocation:{
    type: 'database',
    cellFormatter: createProjectLocationLinkCellFormatter(
      (rowData) => {return rowData.containerId === undefined? null: rowData.containerId;},
      (rowData) => {return rowData.containerName === undefined? null: rowData.containerName;},
      true,
      (rowData) => rowData.id === ''
    ),
  },
};

export const columnsForVizPortal = mapDC3ColumnFactoriesToVizPortalColumnFactories(
  columns,
  makeDC3ColumnFactoryParamsFromVizPortalParams
);

export const databasesWithCrassCellFormatters = cellFormattersForCrass;

export type DatabasesDisplayGridProps = UsualDisplayGridProps<
  DatabasesDisplayGridRowData,
  GraphQLError
> &
  GridSortProps<DatabasesValidSortFields>;

export function DatabasesDisplayGrid(
  props: DatabasesDisplayGridProps
): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);
  const cols = columns
    .map((columnFactory) => columnFactory(props))
    .filter(column => {
      if (column.name === 'virtualConnections' && !publishedConnectionsLineageEnabled) {
        return false;
      }
      return true;
    });
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
